import React from "react";
import { FaArrowDown, FaArrowUp, FaRegClone, FaTrash } from "react-icons/fa6";
import { v4 as uuidv4 } from "uuid";

export default function FormDroppableFunctions({
	item,
	allData,
	setDroppedItems,
	container,
	containerKey,
	repeatingGroup,
	groupKey,
}) {
	const uuid = uuidv4().slice(0, 8);
	const combinedItem = {
		key: `${item.name?.toLowerCase().replace(/\s+/g, "-")}-${uuid}`,
		name: item?.name,
		title: item?.name,
		placeholder: null,
		inputType: item?.inputType || "text",
		css: {},
		required: { value: false, validationMessage: "this field is required" },
		radioOptions:
			item?.name === "Radio_group"
				? [
						{ value: 1, label: "Option A" },
						{ value: 2, label: "Option B" },
						{ value: 3, label: "Option C" },
				  ]
				: null,
		dropdownOptions:
			item?.name === "Dropdown" || item?.name === "MultiSelect"
				? {
						queryKey: "static",
						data: [
							{ value: 1, label: "Option A" },
							{ value: 2, label: "Option B" },
							{ value: 3, label: "Option C" },
						],
				  }
				: null,
		btnType: item?.name === "Button" ? "button" : null,
		hyperLink: null,
		imgPath: null,
		container: item?.name === "Container" ? [] : null,
	fields: item?.name === "RepeatingGroup" ? [] : null,
	groupConfig: item?.name === "RepeatingGroup" ? item?.groupConfig : null,
	readOnly:
		item?.name === "DerivedValue" || item?.name === "ComputedValue" ? true : null,
	derivedConfig: item?.name === "DerivedValue" ? item?.derivedConfig : null,
	computedConfig: item?.name === "ComputedValue" ? item?.computedConfig : null,
	};
	const getIndex = () => {
		return allData.findIndex((i) => i.key === item?.key);
	};

	const removeInput = () => {
    setDroppedItems((prev) => {
        // Remove child from a repeating group
        if (repeatingGroup) {
            return prev.map((group) => {
                if (group.key !== groupKey || !Array.isArray(group.fields)) {
                    return group;
                }

                return {
                    ...group,
                    fields: group.fields.filter(
                        (field) => field.key !== item.key
                    ),
                };
            });
        }

        // Remove child from a container
        if (container) {
            return prev.map((parent) => {
                if (
                    parent.key !== containerKey ||
                    !Array.isArray(parent.container)
                ) {
                    return parent;
                }

                return {
                    ...parent,
                    container: parent.container.filter(
                        (field) => field.key !== item.key
                    ),
                };
            });
        }

        // Remove top-level field
        return prev.filter((field) => field.key !== item.key);
    });
};
	const cloneInput = () => {
		const index = getIndex();

		if (item.name === "Container") {
			// 🔁 Clone full container (with all children)
			setDroppedItems((prev) => {
				const updated = [...prev];
				const original = updated[index];

				// Clone all children
				const clonedChildren = original.container.map((child) => ({
					...child,
					key: `${child.name
						?.toLowerCase()
						.replace(/\s+/g, "-")}-${uuidv4().slice(0, 8)}`,
				}));

				const clonedContainer = {
					...original,
					key: `${original.name
						?.toLowerCase()
						.replace(/\s+/g, "-")}-${uuidv4().slice(0, 8)}`,
					container: clonedChildren,
				};
				console.log("Cloned Container:", clonedContainer);
				updated.splice(index + 1, 0, clonedContainer);
				return updated;
			});
		} else if (item.name === "RepeatingGroup") {
			setDroppedItems((prev) => {
				const updated = [...prev];
				const original = updated[index];

				const clonedChildren = (original.fields || []).map((child) => ({
					...child,
					key: `${child.name
						?.toLowerCase()
						.replace(/\s+/g, "-")}-${uuidv4().slice(0, 8)}`,
				}));

				const clonedGroup = {
					...original,
					key: `${original.name
						?.toLowerCase()
						.replace(/\s+/g, "-")}-${uuidv4().slice(0, 8)}`,
					fields: clonedChildren,
				};
				updated.splice(index + 1, 0, clonedGroup);
				return updated;
			});
		} else if (repeatingGroup) {
			setDroppedItems((prev) => {
				return prev.map((groupItem) => {
					if (
						groupItem.key === groupKey &&
						Array.isArray(groupItem.fields)
					) {
						const itemIndex = groupItem.fields.findIndex(
							(i) => i.key === item.key
						);
						if (itemIndex !== -1) {
							const cloned = {
								...groupItem.fields[itemIndex],
								key: `${item.name
									?.toLowerCase()
									.replace(/\s+/g, "-")}-${uuidv4().slice(0, 8)}`,
							};
							const newFields = [
								...groupItem.fields.slice(0, itemIndex + 1),
								cloned,
								...groupItem.fields.slice(itemIndex + 1),
							];
							return { ...groupItem, fields: newFields };
						}
					}
					return groupItem;
				});
			});
		} else if (container) {
			// 🧩 Clone item inside its container
			setDroppedItems((prev) => {
				return prev.map((containerItem) => {
					if (
						containerItem.key === containerKey &&
						Array.isArray(containerItem.container)
					) {
						const itemIndex = containerItem.container.findIndex(
							(i) => i.key === item.key
						);
						if (itemIndex !== -1) {
							const cloned = {
								...containerItem.container[itemIndex],
								key: `${item.name
									?.toLowerCase()
									.replace(/\s+/g, "-")}-${uuidv4().slice(0, 8)}`,
							};
							const newContainer = [
								...containerItem.container.slice(0, itemIndex + 1),
								cloned,
								...containerItem.container.slice(itemIndex + 1),
							];
							console.log("Cloned Item in Container:", cloned);
							return { ...containerItem, container: newContainer };
						}
					}
					return containerItem;
				});
			});
		} else {
			// 📦 Clone top-level item
			setDroppedItems((prev) => {
				const updated = [...prev];
				const original = updated[index];

				const cloned = {
					...original,
					key: `${original.name
						?.toLowerCase()
						.replace(/\s+/g, "-")}-${uuidv4().slice(0, 8)}`,
				};

				updated.splice(index + 1, 0, cloned);
				return updated;
			});
		}
	};

	const moveForward = (key) => {
		const index = getIndex();
		setDroppedItems((prev) => {
			if (index === -1) return prev;
			const updated = prev.filter((item) => item !== key);
			updated.splice(index + 1, 0, key);
			return updated;
		});
	};
	const moveBackward = (key) => {
		setDroppedItems((prev) => {
			const index = getIndex();
			if (index === -1) return prev;
			const updated = prev.filter((item) => item !== key);
			updated.splice(index - 1, 0, key);
			return updated;
		});
	};
	return (
		<div className="task-droppable-functions">
			<div>{item?.key}</div>
			<FaTrash onClick={removeInput} cursor="pointer" />
			<FaRegClone onClick={() => cloneInput(item.name)} cursor="pointer" />
		</div>
	);
}
